import ProgressivePromise from '@aytm/lib/AsyncJobManager/ProgressivePromise'
import railsPostFetch, { getCSRFToken } from '@aytm/lib/common/rails-post-fetch'
import fetch from 'isomorphic-fetch'
import uploadWithProgress from '@aytm/lib/common/upload-with-progress'
import { headersFromGoogleUploadUrl } from '@aytm/lib/common/google-cloud-url'

export default function VideoAssetUploadJob({ createAssetPath, file, params = {} } = {}) {
  return () =>
    new ProgressivePromise((resolve, reject, progress) => {
      const totalProgress = file.size
      // CREATE ASSET

      progress(0, totalProgress) // set progress to 0

      const createParams = Object.assign({}, params, {
        filename: file.name,
        content_type: file.type,
        filesize: file.size
      })
      const asset = {}

      railsPostFetch(createAssetPath, {
        body: JSON.stringify(createParams)
      })
        .then(response => (response.ok ? response.json() : {}))

        // UPLOAD VIDEO
        .then(createdAsset => {
          Object.assign(asset, createdAsset)

          if (
            !asset.uid ||
            'uploading' !== asset.status ||
            !asset.upload_urls ||
            !asset.upload_urls.binary_upload_url ||
            !asset.upload_urls.finish_upload_url ||
            !asset.upload_urls.status_upload_url
          ) {
            return Promise.reject(new Error('createAsset - invalid response data'))
          }

          return uploadWithProgress(
            asset.upload_urls.binary_upload_url,
            {
              method: 'PUT',
              headers: {
                'Content-Type': file.type,
                ...headersFromGoogleUploadUrl(asset.upload_urls.binary_upload_url)
              },
              body: file
            },
            progressEvent => {
              progress(Math.round(progressEvent.loaded * 0.5), totalProgress) // set progress for uploaded bytes with correction 100% -> 50%
            }
          )
        })

        // FINISH UPLOAD
        .then(() =>
          fetch(asset.upload_urls.finish_upload_url, {
            method: 'POST',
            mode: 'cors',
            headers: {
              'X-CSRF-Token': getCSRFToken()
            }
          })
        )

        // CHECK STATUS
        .then(finishResponse => {
          if (finishResponse.ok) {
            let processingVirtualProgress = 0.55
            progress(Math.round(totalProgress * processingVirtualProgress), totalProgress) // set progress to 55%
            return new Promise((resolve, reject) => {
              const checkStatus = () => {
                fetch(asset.upload_urls.status_upload_url, {
                  method: 'POST',
                  mode: 'cors',
                  headers: {
                    'X-CSRF-Token': getCSRFToken()
                  },
                  Accept: 'application/json'
                })
                  .then(response => (response.ok ? response.json() : {}))
                  .then(assetStatus => {
                    switch (assetStatus.status) {
                      case 'ready':
                      case 'source_valid': {
                        progress(totalProgress, totalProgress) // set progress to 100%
                        resolve(
                          Object.assign({}, asset, assetStatus, {
                            status: 'ready',
                            fileName: file.name,
                            contentType: file.type,
                            fileSize: file.size
                          })
                        )
                        break
                      }
                      case 'processing': {
                        processingVirtualProgress = Math.min(processingVirtualProgress + 0.05, 0.9)
                        progress(totalProgress * processingVirtualProgress, totalProgress) // set progress to processingVirtualProgress%
                        setTimeout(checkStatus, 2000)
                        break
                      }
                      default: {
                        reject(assetStatus)
                        break
                      }
                    }
                  })
                  .catch(_error => {
                    setTimeout(checkStatus, 2000)
                  })
              }
              setTimeout(checkStatus, 2000)
            })
          } else {
            return Promise.reject(new Error('unable to finish upload'))
          }
        })
        .then(resolve, reject)
        .catch(error => reject(error))
    })
}
